<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isFuture, isBefore } from 'date-fns';
import { formatDistanceToNow } from '@/utils/date-fns';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import i18n from '@/utils/i18n';
import { get } from 'lodash';
import CoverView from '@/views/shared/CoverView.vue';
import DateTimeBlock from '@/components/shared/blocks/DateTimeBlock.vue';
import DescriptionBlock from '@/components/shared/blocks/DescriptionBlock.vue';
import DepartmentsBlock from '@/components/shared/blocks/DepartmentsBlock.vue';
import ParticipantsBlock from '@/components/shared/blocks/ParticipantsBlock.vue';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import DeadlineBlock from '@/components/shared/blocks/DeadlineBlock.vue';
import InstitutionBlock from '@/components/shared/blocks/InstitutionBlock.vue';
import Tts from '@/components/shared/Tts.vue';
import { useInstitutionStore } from '@/stores/institution';
import { useDepartmentStore } from '@/stores/department';
import { useMealsStore } from '@/stores/meals';
import constants from '@/utils/constants';
import MediaBlock from '@/components/shared/blocks/MediaBlock.vue';
import DishesBlock from '@/components/shared/blocks/DishesBlock.vue';
import BasicBlock from '@/components/shared/blocks/BasicBlock.vue';
import { IProfile } from '@/interfaces';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';
import { MealEntry, MealParticipantFragment } from '@/graphql/custom-types';
import { PictureFragment } from '@/graphql/generated-types';
import { eventBus } from '@/utils/eventBus';
import RepeatInfoBlock from '@/components/shared/blocks/RepeatInfoBlock.vue';

const institutionStore = useInstitutionStore();
const departmentStore = useDepartmentStore();
const mealsStore = useMealsStore();
const route = useRoute();

const showAuthFlow = ref<boolean>(false);
const participating = ref<boolean>(true);
const ttsKey = ref<number>(0);
const authFlowLoading = ref<boolean>(false);

const moduleColor = computed<string>(() => {
  return route?.meta?.color as string;
});

const useExternalMeals = computed(() => {
  return get(institutionStore.settings, 'useExternalMeals', false);
});

const imageUrl = computed(() => {
  return get(
    mealsStore.selected,
    'coverImage.source',
    constants.placeholderImage
  );
});

const signupPossible = computed(() => {
  if (!get(departmentStore.settings, 'meal.signup', true)) return false;
  return mealsStore.selected.signupPossible && !mealsStore.selected.cancelled;
});

const optoutPossible = computed(() => {
  if (!get(departmentStore.settings, 'meal.signup', true)) return false;
  if (!mealsStore.selected.signupEnd) return false;
  return (
    isFuture(new Date(mealsStore.selected.startDate)) &&
    isBefore(new Date(), new Date(mealsStore.selected.signupEnd))
  );
});

const signupExpired = computed(() => {
  if (!mealsStore.selected.signupEnd) return false;
  return (
    !mealsStore.selected.signupPossible &&
    !isBefore(new Date(), new Date(mealsStore.selected.signupEnd))
  );
});

onMounted(() => {
  mealsStore.getSelectedMeal(route.params.id as string);
});

function formatDeadline(meal: MealEntry) {
  if (!meal.signupEnd) {
    return '';
  }

  return `${i18n.global.t('global.signUpDeadline')} ${formatDistanceToNow(new Date(meal.signupEnd), { addSuffix: true })}`;
}

const pinOverlayTitle = computed(() => {
  return participating.value
    ? i18n.global.t('meals.signUp')
    : i18n.global.t('meals.optOut');
});

const pinOverlayTitleBold = computed(() => {
  return mealsStore.selected.title;
});

const successOverlayTitle = computed(() => {
  return participating.value
    ? i18n.global.t('meals.signedUp')
    : i18n.global.t('meals.optedOut');
});

async function setParticipating(isParticipating: boolean) {
  participating.value = isParticipating;
  showAuthFlow.value = true;
}

const save = async (profile: IProfile, pin: string) => {
  try {
    authFlowLoading.value = true;

    const signupData = {
      subscription: {
        id: mealsStore.selected.id,
      },
      profile: {
        profileId: profile.id,
        profileType: profile.type,
        pin,
      },
      participating: participating.value,
    };

    await mealsStore.mealSignupScreen(signupData);

    eventBus.emit('success', successOverlayTitle.value);
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showAuthFlow.value = false;
  }
};

// need to refresh TTS when signupPossible changes (changes the deadline block)
watch(
  [signupPossible, () => mealsStore.selected.participants],
  () => {
    ttsKey.value += 1;
  },
  { deep: true }
);

const allImages = computed(() => {
  return [
    ...(mealsStore.selected.dishes || [])
      .filter((dish) => dish.picture)
      .map((dish) => dish.picture as PictureFragment),
    ...(mealsStore.selected.pictures || []),
  ];
});

const selectedParticipants = computed(() => {
  const participants = mealsStore.selected.participants || [];
  return participants.filter(
    (participant): participant is MealParticipantFragment =>
      participant != null &&
      (participant.__typename === 'Employee' ||
        participant.__typename === 'Resident')
  );
});
</script>

<template>
  <div class="meal" v-if="mealsStore.selected">
    <CoverView
      v-if="mealsStore.selected && mealsStore.selected.id"
      :image-url="imageUrl"
      :module-color="moduleColor"
    >
      <div class="mealContent tts-content">
        <div class="flex items-start justify-between">
          <TitleBlock :text="mealsStore.selected.title" />
          <Tts />
        </div>

        <MediaBlock
          v-if="!useExternalMeals"
          :pictures="allImages"
          :documents="mealsStore.selected.documents || []"
          :color="moduleColor"
        />

        <div class="mealDetails">
          <BlocksWrapper>
            <InstitutionBlock
              :title="mealsStore.selected.institution?.name"
              :image="mealsStore.selected.institution?.logo?.source"
              v-if="
                institutionStore.isExternalInstitution(
                  mealsStore.selected.institution?.id
                )
              "
            />

            <DateTimeBlock
              v-if="!useExternalMeals"
              :start="mealsStore.selected.startDate"
              :end="mealsStore.selected.endDate ?? ''"
            />

            <RepeatInfoBlock :event="mealsStore.selected" />

            <DishesBlock :dishes="mealsStore.selected.dishes ?? []" />

            <DescriptionBlock
              v-if="(mealsStore.selected.description ?? '').length > 0"
              :description="mealsStore.selected.description ?? ''"
              :icon="['fas', 'fa-align-left']"
            />

            <DeadlineBlock
              v-if="signupPossible"
              :signup-expired="signupExpired"
              :text="formatDeadline(mealsStore.selected)"
            />

            <ParticipantsBlock
              v-if="
                mealsStore.selected.participants?.length && !useExternalMeals
              "
              :profiles="selectedParticipants"
              canTriggerOverlay
              participant-type="participant"
            />

            <ParticipantsBlock
              v-if="mealsStore.selected.responsible?.length"
              participant-type="responsible"
              canTriggerOverlay
              :profiles="mealsStore.selected.responsible"
            />

            <DepartmentsBlock
              v-if="
                mealsStore.selected.departments?.length && !useExternalMeals
              "
              :departments="mealsStore.selected.departments"
            />

            <BasicBlock
              v-if="
                !institutionStore.isExternalInstitution(
                  mealsStore.selected.institution?.id
                )
              "
              :icon="['fal', 'fa-hotel']"
              :text="mealsStore.selected.institution.name"
              :borderBottom="false"
            />
          </BlocksWrapper>
        </div>
      </div>

      <div class="mealActions w-max left-full sticky">
        <ButtonEl
          v-if="signupPossible"
          text="global.signUp"
          :icon="['far', 'circle-plus']"
          background="bg-green-400"
          textColor="text-neutral-0"
          class="shadow-xsm w-51.25 mb-4"
          rounded="full"
          @click="setParticipating(true)"
        />

        <ButtonEl
          v-if="optoutPossible"
          text="global.optOut"
          :icon="['far', 'minus-circle']"
          background="bg-red-400"
          textColor="text-neutral-0"
          class="shadow-xsm w-51.25"
          rounded="full"
          @click="setParticipating(false)"
        />
      </div>
    </CoverView>

    <AuthFlow
      v-if="showAuthFlow"
      :participating="participating"
      :shared-departments="mealsStore.selected.departments || []"
      :participants="selectedParticipants"
      :title-success="successOverlayTitle"
      :title="pinOverlayTitle"
      :title-bold="pinOverlayTitleBold"
      :loading="authFlowLoading"
      @close="showAuthFlow = false"
      @authenticated="(profile, pin) => save(profile, pin)"
    />
  </div>
</template>

<style lang="scss" scoped>
.meal {
  .mealActions {
    bottom: 70px;
    padding-right: 80px;
  }
}
</style>
